import { ADDRESSES, Period } from '@kwenta/sdk/constants'
import {
	FuturesMarketAsset,
	FuturesMarketKey,
	PositionSide,
	TransactionStatus,
} from '@kwenta/sdk/types'
import { wei } from '@kwenta/wei'
import { createAsyncThunk } from '@reduxjs/toolkit'
import { AbstractionToken } from 'services/biconomy/accountAbstraction.types'
import { Address, WalletClient, formatEther, formatUnits, parseEther, parseUnits } from 'viem'

import { EST_TRADE_TX_COST_USDC, IS_ONE_CLICK_TRADING_ENABLED } from 'constants/defaults'
import { monitorAndAwaitTransaction, monitorAndAwaitUserOp } from 'state/app/helpers'
import {
	handleTransactionError,
	setOpenModal,
	setTransaction,
	updateTransactionStatus,
} from 'state/app/reducer'
import { fetchWalletEthBalance } from 'state/balances/actions'
import {
	clearCrossMarginTradeInputs,
	editCloseCMPositionPrice,
	editCrossMarginCloseAmount,
	editCrossMarginPositionSize,
	editCrossMarginTradeOrderPrice,
	editCrossMarginTradeSize,
	fetchDailyVolumesV3,
	fetchFundingRatesHistoryV3,
	fetchMarginTransfersV3,
	fetchPositionHistoryV3,
	fetchPositionHistoryV3ForTrader,
	fetchV3Markets,
} from 'state/futures/snxPerpsV3/actions'
import { AppThunk } from 'state/store'
import { ThunkConfig } from 'state/types'
import { setAbstractionAddress } from 'state/wallet/reducer'
import { selectAbstractionAddress, selectSignerNetwork, selectWallet } from 'state/wallet/selectors'
import { DateInterval } from 'utils/dates'
import logError from 'utils/logError'

import { selectPerpsProvider, selectTradingMode } from './common/selectors'
import {
	setAbstractionEthBalance,
	setAbstractionTxCost,
	setAbstractionUsdcBalance,
	setDelegated,
	setEditPositionInputs,
	setSelectedMarketAsset,
	setSession,
	setTradeStopLoss,
} from './reducer'
import { selectAbstractionDelegated, selectSessionExpiry } from './selectors'
import {
	clearSmartMarginTradeInputs,
	editCloseSMPositionPrice,
	editCloseSMPositionSizeDelta,
	editSmartMarginPositionSize,
	editSmartMarginTradeOrderPrice,
	editSmartMarginTradeSize,
	fetchDailyVolumesV2,
	fetchFundingRatesHistoryV2,
	fetchMarginTransfersV2,
	fetchMarketsV2,
	fetchPositionHistoryV2,
	fetchPositionHistoryV2ForTrader,
	fetchSmartMarginOpenOrders,
} from './snxPerpsV2/actions'
import { selectSmartMarginAccount } from './snxPerpsV2/selectors'
import { selectCrossMarginAccount } from './snxPerpsV3/selectors'
import { PerpsProvider, TradingModes } from './types'

export const setMarketAsset = createAsyncThunk<void, FuturesMarketAsset, ThunkConfig>(
	'futures/fetchMarkets',
	async (asset, { dispatch, getState }) => {
		const provider = selectPerpsProvider(getState())
		dispatch(clearTradeInputs())
		dispatch(setSelectedMarketAsset({ asset, provider }))
	}
)

export const fetchMarkets = createAsyncThunk<void, void, ThunkConfig>(
	'futures/fetchMarkets',
	async (_, { dispatch, getState }) => {
		if (getState().futures.preferences.selectedProvider === PerpsProvider.SNX_V2) {
			dispatch(fetchMarketsV2())
		} else {
			dispatch(fetchV3Markets())
		}
	}
)

export const fetchDailyVolumes = createAsyncThunk<void, void, ThunkConfig>(
	'futures/fetchDailyVolumes',
	async (_, { dispatch, getState }) => {
		if (getState().futures.preferences.selectedProvider === PerpsProvider.SNX_V2) {
			dispatch(fetchDailyVolumesV2())
		} else {
			dispatch(fetchDailyVolumesV3())
		}
	}
)

export const fetchMarginTransfers = createAsyncThunk<void, void, ThunkConfig>(
	'futures/fetchMarginTransfers',
	async (_, { dispatch, getState }) => {
		if (getState().futures.preferences.selectedProvider === PerpsProvider.SNX_V2) {
			dispatch(fetchMarginTransfersV2())
		} else {
			dispatch(fetchMarginTransfersV3())
		}
	}
)

export const clearTradeInputs = createAsyncThunk<void, void, ThunkConfig>(
	'futures/clearTradeInputs',
	async (_, { dispatch }) => {
		dispatch(clearSmartMarginTradeInputs())
		dispatch(clearCrossMarginTradeInputs())
	}
)

export const setTradeStopLossAction = createAsyncThunk<void, string, ThunkConfig>(
	'futures/setTradeStopLoss',
	async (price, { dispatch }) => {
		dispatch(setTradeStopLoss(price))
	}
)

export const setLeverageSide = createAsyncThunk<void, PositionSide, ThunkConfig>(
	'futures/setLeverageSide',
	async (side, { dispatch }) => {
		dispatch(setLeverageSide(side))
	}
)

export const editTradePanelOrderPrice =
	(price: string): AppThunk =>
	(dispatch, getState) => {
		const type = selectPerpsProvider(getState())
		if (type === PerpsProvider.SNX_V3) {
			dispatch(editCrossMarginTradeOrderPrice(price))
		} else {
			dispatch(editSmartMarginTradeOrderPrice(price))
		}
	}

export const editTradeSizeInput =
	(size: string, currencyType: 'usd' | 'native'): AppThunk =>
	(dispatch, getState) => {
		const type = selectPerpsProvider(getState())
		if (type === PerpsProvider.SNX_V3) {
			dispatch(editCrossMarginTradeSize(size, currencyType))
		} else {
			dispatch(editSmartMarginTradeSize(size, currencyType))
		}
	}

export const fetchFuturesPositionHistory = createAsyncThunk<void, void, ThunkConfig>(
	'futures/fetchFuturesPositionHistory',
	async (_, { getState, dispatch }) => {
		const perpsProvider = selectPerpsProvider(getState())
		perpsProvider === PerpsProvider.SNX_V3
			? dispatch(fetchPositionHistoryV3())
			: dispatch(fetchPositionHistoryV2())
	}
)

export const fetchPositionHistoryForTrader = createAsyncThunk<void, string, ThunkConfig>(
	'futures/fetchPositionHistoryForTrader',
	async (traderAddress, { getState, dispatch }) => {
		const perpsProvider = selectPerpsProvider(getState())
		perpsProvider === PerpsProvider.SNX_V3
			? dispatch(fetchPositionHistoryV3ForTrader(traderAddress))
			: dispatch(fetchPositionHistoryV2ForTrader(traderAddress))
	}
)

// Contract Mutations

export const cancelDelayedOrder = createAsyncThunk<void, string, ThunkConfig>(
	'futures/cancelDelayedOrder',
	async (marketAddress, { getState, dispatch, extra: { sdk } }) => {
		const account = selectSmartMarginAccount(getState())
		if (!account) throw new Error('No wallet connected')
		try {
			dispatch(
				setTransaction({
					status: TransactionStatus.AwaitingExecution,
					type: 'cancel_delayed_isolated',
					hash: null,
				})
			)

			const tx = await sdk.futures.cancelDelayedOrder({
				marketAddress: marketAddress as Address,
				account,
				isOffchain: true,
			})
			await monitorAndAwaitTransaction(dispatch, tx)
			dispatch(fetchSmartMarginOpenOrders())
		} catch (err) {
			dispatch(handleTransactionError({ message: err.message }))
			throw err
		}
	}
)

// Utils

export const fetchFundingRatesHistory = createAsyncThunk<
	void,
	{ marketAsset: FuturesMarketAsset; period: Period },
	ThunkConfig
>('futures/fetchFundingRatesHistory', ({ marketAsset, period }, { getState, dispatch }) => {
	const perpsProvider = selectPerpsProvider(getState())
	perpsProvider === PerpsProvider.SNX_V3
		? dispatch(fetchFundingRatesHistoryV3({ marketAsset, period }))
		: dispatch(fetchFundingRatesHistoryV2({ marketAsset, period }))
})

// One-Click Trading
export const delegateToOneClickTrading = createAsyncThunk<void, void, ThunkConfig>(
	'futures/delegateToOneClickTrading',
	async (_, { getState, dispatch, extra: { accountAbstraction, sdk } }) => {
		const address = accountAbstraction?.accountAddress
		const smAccount = selectSmartMarginAccount(getState())
		const futuresType = selectPerpsProvider(getState())
		const accountId = selectCrossMarginAccount(getState())

		if (!address) throw new Error('No abstraction address connected')
		if (!smAccount && futuresType === PerpsProvider.SNX_V2)
			throw new Error('No smart margin account connected')
		if (!accountId && futuresType === PerpsProvider.SNX_V3)
			throw new Error('No cross margin account connected')

		try {
			dispatch(
				setTransaction({
					status: TransactionStatus.AwaitingExecution,
					type: 'delegate_to_smart_margin',
					hash: null,
				})
			)
			const tx =
				futuresType === PerpsProvider.SNX_V2
					? await sdk.futures.delegateSmartMarginAccount({
							account: smAccount!,
							delegate: address,
					  })
					: await sdk.perpsV3.grantDelegatePermission(accountId!, address)

			await monitorAndAwaitTransaction(dispatch, tx)
			dispatch(setDelegated(true))
		} catch (err) {
			dispatch(handleTransactionError({ message: err.message }))
			dispatch(fetchAbstractionDelegateStatus())
		}
	}
)

export const startOneClickSession = createAsyncThunk<void, DateInterval | number, ThunkConfig>(
	'futures/delegateToOneClickTrading',
	async (interval, { getState, dispatch, extra: { accountAbstraction, sdk } }) => {
		const address = accountAbstraction?.accountAddress
		const smAccount = selectSmartMarginAccount(getState())
		const futuresType = selectPerpsProvider(getState())
		const accountId = selectCrossMarginAccount(getState())

		const engineAddress = sdk.context.defaultContractConfigs.MarginEngine?.address

		const isCross = futuresType === PerpsProvider.SNX_V3

		if (!address) throw new Error('No abstraction address connected')
		if (!smAccount && !isCross) throw new Error('No smart margin account connected')
		if ((!accountId || !engineAddress) && isCross)
			throw new Error('No cross margin account connected')

		try {
			dispatch(
				setTransaction({
					status: TransactionStatus.AwaitingExecution,
					type: 'create_session',
					hash: null,
				})
			)

			const session = await accountAbstraction.createSession(
				interval,
				isCross ? engineAddress! : smAccount!
			)

			dispatch(setSession(session))
			dispatch(updateTransactionStatus(TransactionStatus.Confirmed))
		} catch (err) {
			logError(err)
			dispatch(handleTransactionError({ message: err.message }))
		}
	}
)

export const closeOneClickSession = createAsyncThunk<void, void, ThunkConfig>(
	'futures/delegateToOneClickTrading',
	async (_, { getState, dispatch, extra: { accountAbstraction } }) => {
		const address = accountAbstraction?.accountAddress
		const smAccount = selectSmartMarginAccount(getState())
		const futuresType = selectPerpsProvider(getState())
		const accountId = selectCrossMarginAccount(getState())

		const isCross = futuresType === PerpsProvider.SNX_V3

		if (!address) throw new Error('No abstraction address connected')
		if (!smAccount && !isCross) throw new Error('No smart margin account connected')
		if (!accountId && isCross) throw new Error('No cross margin account connected')

		try {
			dispatch(
				setTransaction({
					status: TransactionStatus.AwaitingExecution,
					type: 'close_session',
					hash: null,
				})
			)

			const session = await accountAbstraction.closeAllSessions()

			dispatch(setSession(session))
			dispatch(updateTransactionStatus(TransactionStatus.Confirmed))
		} catch (err) {
			logError(err)
			dispatch(handleTransactionError({ message: err.message }))
		}
	}
)

export const depositFundsToOneClick = createAsyncThunk<
	void,
	{ amount: string; token: AbstractionToken },
	ThunkConfig
>(
	'futures/depositFundsToOneClick',
	async ({ amount, token }, { getState, dispatch, extra: { sdk } }) => {
		const state = getState()
		const network = selectSignerNetwork(state)
		const abstractionAddress = selectAbstractionAddress(state)

		const walletClient = sdk.context.walletClient

		if (!walletClient || !abstractionAddress || !network) throw new Error('No account connected')

		try {
			dispatch(
				setTransaction({
					status: TransactionStatus.AwaitingExecution,
					type: 'deposit_one_click',
					hash: null,
				})
			)

			if (token === AbstractionToken.ETH) {
				const tx = await walletClient.sendTransaction({
					to: abstractionAddress,
					value: parseEther(amount),
				})

				await monitorAndAwaitTransaction(dispatch, tx)
				dispatch(fetchAbstractionEthBalance())
			} else if (token === AbstractionToken.USDC) {
				const request = await sdk.tokens.transferToken(
					ADDRESSES[AbstractionToken.USDC][network],
					abstractionAddress,
					parseUnits(amount, sdk.exchange.usdcDecimals)
				)

				const tx = await walletClient.writeContract(request)

				await monitorAndAwaitTransaction(dispatch, tx)
				dispatch(fetchAbstractionUsdcBalance())
			}
		} catch (err) {
			dispatch(handleTransactionError({ message: err.message }))
		}
	}
)

export const withdrawFundsFromOneClick = createAsyncThunk<
	void,
	{ amount: string; token: AbstractionToken },
	ThunkConfig
>(
	'futures/withdrawFundsFromOneClick',
	async ({ amount, token }, { getState, dispatch, extra: { accountAbstraction, sdk } }) => {
		const accountAddress = selectWallet(getState())
		const abstractionAddress = selectAbstractionAddress(getState())
		const network = selectSignerNetwork(getState())
		const walletClient = sdk.context.walletClient

		try {
			if (
				!walletClient ||
				!accountAbstraction ||
				!abstractionAddress ||
				!accountAddress ||
				!network
			)
				throw new Error('No account connected')

			dispatch(
				setTransaction({
					status: TransactionStatus.AwaitingExecution,
					type: 'withdraw_one_click',
					hash: null,
				})
			)

			if (token === AbstractionToken.ETH) {
				const tx = {
					to: accountAddress,
					value: parseEther(amount),
				}

				const res = await accountAbstraction.sendTransactions({
					readyTxs: [tx],
					options: {},
				})

				await monitorAndAwaitUserOp(dispatch, res)
				dispatch(fetchAbstractionEthBalance())
				dispatch(fetchWalletEthBalance())
			} else if (token === AbstractionToken.USDC) {
				const request = await sdk.tokens.transferToken(
					ADDRESSES[AbstractionToken.USDC][network],
					accountAddress,
					parseUnits(amount, sdk.exchange.usdcDecimals),
					abstractionAddress
				)

				const tx = await accountAbstraction.sendTransactions({
					simulateTxs: [request],
					options: {},
				})

				await monitorAndAwaitUserOp(dispatch, tx)
				dispatch(fetchAbstractionUsdcBalance())
			}
		} catch (err) {
			dispatch(handleTransactionError({ message: err.message }))
		}
	}
)

export const initAccountAbstraction = createAsyncThunk<void, WalletClient | undefined, ThunkConfig>(
	'futures/initAccountAbstraction',
	async (client, { dispatch, getState, extra: { sdk, accountAbstraction } }) => {
		const futuresMode = selectPerpsProvider(getState())
		const publicClient = sdk.context.client
		if (!accountAbstraction) return
		if (client) {
			const isPaymasterEnabled = futuresMode === PerpsProvider.SNX_V3
			await accountAbstraction.init(client, publicClient, isPaymasterEnabled)
			const { accountAddress } = accountAbstraction

			if (!accountAddress) return

			const session = await accountAbstraction.getSessionInfo()

			dispatch(setSession(session))
			dispatch(setAbstractionAddress(accountAddress))
		} else {
			accountAbstraction.disconnect()
		}
	}
)

export const fetchAbstractionDelegateStatus = createAsyncThunk<void, void, ThunkConfig>(
	'futures/fetchAbstractionDelegateStatus',
	async (_, { getState, dispatch, extra: { sdk } }) => {
		const abstractionAddress = selectAbstractionAddress(getState())
		const smAddress = selectSmartMarginAccount(getState())
		const futuresType = selectPerpsProvider(getState())
		const accountId = selectCrossMarginAccount(getState())

		if (!abstractionAddress) throw new Error('No abstraction address connected')
		if (!smAddress && futuresType === PerpsProvider.SNX_V2)
			throw new Error('No smart margin account connected')
		if (!accountId && futuresType === PerpsProvider.SNX_V3)
			throw new Error('No cross margin account connected')

		const isDelegate =
			futuresType === PerpsProvider.SNX_V2
				? await sdk.futures.checkDelegateSmartMarginAccount({
						account: smAddress!,
						delegate: abstractionAddress,
				  })
				: await sdk.perpsV3.checkDelegatePermission(accountId!, abstractionAddress)

		dispatch(setDelegated(isDelegate))
	}
)

export const handleOneClickOperation = createAsyncThunk<void, VoidFunction, ThunkConfig>(
	'futures/handleOneClickOperation',
	async (callback, { getState, dispatch }) => {
		const tradingMode = selectTradingMode(getState())
		const isSessionActive = selectSessionExpiry(getState()) > Math.floor(Date.now() / 1000)
		const isDelegated = selectAbstractionDelegated(getState())

		const isNeedActivate = !isSessionActive && tradingMode === TradingModes.ONE_CLICK

		try {
			if (IS_ONE_CLICK_TRADING_ENABLED) {
				if (isNeedActivate) {
					if (isDelegated) {
						await dispatch(startOneClickSession('1D'))
					} else {
						dispatch(setOpenModal('one_click_trading_onboard'))
						return
					}
				}
			}
			return callback()
		} catch (err) {
			dispatch(handleTransactionError({ message: err.message }))
		}
	}
)

export const fetchAbstractionEthBalance = createAsyncThunk<void, void, ThunkConfig>(
	'futures/fetchAbstractionEthBalance',
	async (_, { dispatch, extra: { accountAbstraction } }) => {
		if (!accountAbstraction) return
		const balance = await accountAbstraction.getBalance()

		dispatch(setAbstractionEthBalance(formatEther(balance)))
	}
)

export const fetchAbstractionUsdcBalance = createAsyncThunk<void, void, ThunkConfig>(
	'futures/fetchAbstractionUsdcBalance',
	async (_, { dispatch, extra: { sdk, accountAbstraction } }) => {
		if (!accountAbstraction) return
		const balance = await accountAbstraction.getUsdcBalance()

		const { usdcDecimals } = sdk.exchange

		dispatch(setAbstractionUsdcBalance(formatUnits(balance, usdcDecimals)))
	}
)

export const editPositionSize =
	(marketKey: FuturesMarketKey, nativeSizeDelta: string): AppThunk =>
	(dispatch, getState) => {
		const type = selectPerpsProvider(getState())
		dispatch(
			setEditPositionInputs({
				marginDelta: '',
				nativeSizeDelta: nativeSizeDelta,
			})
		)
		if (type === PerpsProvider.SNX_V3) {
			dispatch(editCrossMarginPositionSize(marketKey, nativeSizeDelta))
		} else {
			dispatch(editSmartMarginPositionSize(marketKey, nativeSizeDelta))
		}
	}

export const editClosePositionPrice =
	(price: string): AppThunk =>
	(dispatch, getState) => {
		const type = selectPerpsProvider(getState())
		if (type === PerpsProvider.SNX_V3) {
			dispatch(editCloseCMPositionPrice(price))
		} else {
			dispatch(editCloseSMPositionPrice(price))
		}
	}

export const editClosePositionSizeDelta =
	(sizeDelta: string): AppThunk =>
	(dispatch, getState) => {
		const type = selectPerpsProvider(getState())
		if (type === PerpsProvider.SNX_V3) {
			dispatch(editCrossMarginCloseAmount(sizeDelta))
		} else {
			dispatch(editCloseSMPositionSizeDelta(sizeDelta))
		}
	}

export const fetchAvgOneClickTxConst = createAsyncThunk<void, void, ThunkConfig>(
	'futures/fetchAvgOneClickTxConst',
	async (_, { dispatch, getState, extra: { accountAbstraction, sdk } }) => {
		if (!accountAbstraction) return

		const avgGas = await accountAbstraction.estimateAvgTxCost()

		const type = selectPerpsProvider(getState())
		const isPaymasterEnabled = type === PerpsProvider.SNX_V3

		if (isPaymasterEnabled) {
			try {
				const ethPrice = await sdk.prices.getOffchainPrice(FuturesMarketAsset.sETH)
				const txCost = ethPrice.mul(avgGas).toNumber()
				dispatch(
					setAbstractionTxCost({
						value: txCost.toFixed(3),
						asset: AbstractionToken.USDC,
					})
				)
			} catch (e) {
				logError(e)
				dispatch(
					setAbstractionTxCost({
						value: EST_TRADE_TX_COST_USDC.toString(),
						asset: AbstractionToken.USDC,
					})
				)
			}
		} else {
			dispatch(
				setAbstractionTxCost({
					value: wei(avgGas).toString(),
					asset: AbstractionToken.USDC,
				})
			)
		}
	}
)
